import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { PpcButton } from '@design-system/button/button';
import { PpcHeading } from '@design-system/heading/heading';

@Component({
  selector: 'app-slogan',
  imports: [TranslatePipe, PpcButton, PpcHeading],
  templateUrl: './slogan.html',
  styleUrl: './slogan.css',
})
export class SloganComponent implements AfterViewInit {

  readonly joinClick = output<void>();

  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly host = viewChild.required<ElementRef<HTMLElement>>('host');

  protected readonly shown = signal(false);

  private readonly COLORED_TARGETS = new Set(['0|0', '1|1', '2|2']);
  private readonly BASE_DELAY_MS = 120;
  private readonly DELAY_STEP_MS = 180;
  private readonly INTERSECTION_THRESHOLD = 0.15;
  private readonly WORD_PATTERN = /^[A-Za-zÀ-ÿ]+$/;
  private readonly TOKEN_SPLIT_PATTERN = /(\s+|[.,!?])/;

  private readonly lines = toSignal(this.translate.stream('slogan.lines') as Observable<string[]>, {
    initialValue: [] as string[],
  });

  protected readonly viewLines = computed(() => {
    const lines = this.lines();
    return lines.map((line, lineIndex) => ({
      segments: this.toSegments(line, lineIndex),
      delayMs: this.BASE_DELAY_MS + lineIndex * this.DELAY_STEP_MS,
      last: lineIndex === lines.length - 1,
    }));
  });

  ngAfterViewInit() {
    const element = this.host().nativeElement;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.shown.set(true);
          observer.disconnect();
        }
      },
      { threshold: this.INTERSECTION_THRESHOLD },
    );
    observer.observe(element);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  private toSegments(line: string, lineIndex: number): { text: string; colored: boolean }[] {
    const segments: { text: string; colored: boolean }[] = [];
    let wordCount = 0;
    const tokens = line.split(this.TOKEN_SPLIT_PATTERN).filter((token) => token.length > 0);
    for (const token of tokens) {
      const isWord = this.WORD_PATTERN.test(token);
      const colored = isWord && this.COLORED_TARGETS.has(`${lineIndex}|${wordCount}`);
      segments.push({ text: token, colored });
      if (isWord) wordCount++;
    }
    return segments;
  }
}
